import React, { useCallback, useEffect, useState } from 'react'
import {
  WbTooltip,
  WbInput,
  WbTooltipHost,
  WbRadioControl,
  WbInputControl,
} from '@workbench/react/dist'
import { Button, PromptModal } from 'components'
import { Booking } from 'api/types'
import { useCancelBooking } from 'api'
import { isCancelCloseDate } from 'utils/bookingDateUtils'
import { useModal } from 'hooks/useModal'
import { useTranslation } from 'react-i18next'

interface CancelBookingButtonProps {
  booking: Booking
}

export const CancelBookingButton: React.FunctionComponent<CancelBookingButtonProps> =
  ({ booking }) => {
    const { t } = useTranslation()

    const [showModal, openModal, closeModal] = useModal()
    const [mutate, { status, reset }] = useCancelBooking()
    const [bookingReason, setBookingReason] = useState('')
    const [disable, setDisable] = useState(true)
    const [input, setInput] = useState(false)
    const is_status = ['CANCELLED', 'SENDING', 'CANCELLING']
    const isClose = isCancelCloseDate(new Date(booking.startDate))

    const disabled = () => {
      if (
        is_status.includes(booking.status) ||
        status === 'success' ||
        isClose
      ) {
        return true
      }
      return false
    }
    let tooltip = null
    if (status !== 'success') {
      if (booking.status === 'SENDING')
        tooltip =
          'pages.bookings.bookingsPage.cancelBookingButton.tooltipDisallowedSending'
      else if (
        booking.status === 'CANCELLING' ||
        booking.status === 'CANCELLED'
      )
        tooltip =
          'pages.bookings.bookingsPage.cancelBookingButton.tooltipDisallowedCancelling'
      else if (isClose)
        tooltip =
          'pages.bookings.bookingsPage.cancelBookingButton.tooltipDisallowedTimeframe'
    }

    const onClick = useCallback(async () => {
      mutate({ bookingId: booking.id, bookingReason: bookingReason })
      closeModal()
    }, [mutate, booking.id, closeModal, bookingReason])

    const cancelModal = () => {
      setReasons('')
      setDisable(true)
      setInput(false)
      closeModal()
    }
    const button = (
      <Button
        size="small"
        variant="secondary"
        onClick={openModal}
        disabled={disabled()}
      >
        {t('pages.bookings.bookingsPage.cancelBookingButton.cancel')}
      </Button>
    )

    const setReasons = (cancelReason: string) => {
      setBookingReason(cancelReason)
      setDisable(false)
      setInput(false)
    }
    const reasonChange = (event: any) => {
      setBookingReason(event.target.value)
      setDisable(false)
    }
    useEffect(() => {
      if (bookingReason === '') {
        setDisable(true)
      }
    }, [bookingReason])

    return (
      <>
        {tooltip ? (
          <WbTooltipHost>
            {button}
            <WbTooltip>{t(tooltip)}</WbTooltip>
          </WbTooltipHost>
        ) : (
          <>{button}</>
        )}

        {showModal && (
          <PromptModal
            headline={t(
              'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelHeadline'
            )}
            cancelCallback={cancelModal}
            cancelText={t(
              'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelButton'
            )}
            confirmCallback={onClick}
            confirmText={t(
              'pages.bookings.bookingsPage.cancelBookingButton.promptModal.confirmButton'
            )}
            disable={disable}
          >
            <p>
              {t(
                'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelMessage'
              )}
            </p>

            {t(
              'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancellingMessage'
            )}
            <WbRadioControl>
              <label>
                <input
                  type="radio"
                  name="cancelReason"
                  onChange={() =>
                    setReasons(
                      `${t(
                        'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelReason.MISSING_RELEASE'
                      )}`
                    )
                  }
                />
                {t(
                  'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelReason.MISSING_RELEASE'
                )}
              </label>
            </WbRadioControl>

            <WbRadioControl>
              <label>
                <input
                  type="radio"
                  name="cancelReason"
                  onChange={() =>
                    setReasons(
                      `${t(
                        'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelReason.VEHICLES_NOT_NEEDED'
                      )}`
                    )
                  }
                />
                {t(
                  'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelReason.VEHICLES_NOT_NEEDED'
                )}
              </label>
            </WbRadioControl>
            <WbRadioControl>
              <label>
                <input
                  type="radio"
                  name="cancelReason"
                  onChange={() =>
                    setReasons(
                      `${t(
                        'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelReason.DUPLICATE_REQUEST'
                      )}`
                    )
                  }
                />
                {t(
                  'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelReason.DUPLICATE_REQUEST'
                )}
              </label>
            </WbRadioControl>
            <WbRadioControl>
              <label>
                <input
                  type="radio"
                  name="cancelReason"
                  onChange={() => setInput(true)}
                />
                {t(
                  'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelReason.MISCELLANEOUS'
                )}
              </label>
            </WbRadioControl>
            {input ? (
              <WbInputControl style={{ marginTop: '10px' }}>
                <WbInput>
                  <label>
                    {t(
                      'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancellingReason'
                    )}
                  </label>
                  <input
                    type="text"
                    name="cancelReason"
                    maxLength={140}
                    onChange={(event) => reasonChange(event)}
                    required
                    value={bookingReason}
                  />
                </WbInput>
              </WbInputControl>
            ) : (
              ''
            )}
          </PromptModal>
        )}

        {status === 'success' && (
          <PromptModal
            headline={t(
              'pages.bookings.bookingsPage.cancelBookingButton.promptModal.successHeadline'
            )}
            confirmCallback={reset}
            confirmText={t('global.back')}
          >
            {t(
              'pages.bookings.bookingsPage.cancelBookingButton.promptModal.cancelOnline'
            )}
          </PromptModal>
        )}

        {status === 'failure' && (
          <PromptModal
            headline={t(
              'pages.bookings.bookingsPage.cancelBookingButton.promptModal.failureHeadline'
            )}
            confirmCallback={reset}
            confirmText={t('global.back')}
          >
            {t(
              'pages.bookings.bookingsPage.cancelBookingButton.promptModal.failureMessage'
            )}
          </PromptModal>
        )}
      </>
    )
  }
